import {QueryCacheKeys} from '../../api/cache';
import {createCustomer, getCustomer, updateCustomer} from '../../api/customers';
import {useTextInputState} from '../../hooks';
import Layout from '../Layout';
import AddressAutocomplete from './AddressAutocomplete';
import {Box, Button, FormField, Input, LoaderButton, Textarea} from '@sproutsocial/racine';
import {useMutation, useQuery} from '@tanstack/react-query';
import {useCallback, useEffect, useMemo, useRef} from 'react';

interface CustomerEditProps {
	customerId: string | null;
}

const CustomerEdit = ({customerId}: CustomerEditProps) => {
	const {data: customer, refetch: refetchCustomer} = useQuery({
		queryKey: QueryCacheKeys.customer(customerId!),
		queryFn: () => getCustomer(customerId!),
		enabled: Boolean(customerId),
	});

	const [name, onNameChange] = useTextInputState(customer?.name ?? '');
	const [email, onEmailChange] = useTextInputState(customer?.email ?? '');
	const [phoneNumber, onPhoneNumberChange] = useTextInputState(customer?.phoneNumber ?? '');
	const [notes, onNotesChange] = useTextInputState(customer?.notes ?? '');
	const [address, onAddressChange] = useTextInputState(customer?.address ?? '');
	const saveDisabled = useMemo(() => {
		// Check required fields
		if (!name.trim()) {
			return true;
		}

		if (customer) {
			// If all fields are the same, disable save
			if (
				customer.name === name.trim() &&
				(customer.email ?? '') === email.trim() &&
				(customer.phoneNumber ?? '') === phoneNumber.trim() &&
				(customer.notes ?? '') === notes.trim() &&
				(customer.address ?? '') === address.trim()
			) {
				return true;
			}
		}

		return false;
	}, [customer, name, email, phoneNumber, notes, address]);

	const customerRef = useRef(customer);
	useEffect(() => {
		if (customer && customerRef.current !== customer) {
			customerRef.current = customer;

			onNameChange(null, customer.name);
			onEmailChange(null, customer.email ?? undefined);
			onPhoneNumberChange(null, customer.phoneNumber ?? undefined);
			onNotesChange(null, customer.notes ?? undefined);
			onAddressChange(null, customer.address ?? undefined);
		}
	}, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

	const {mutateAsync: createCustomerAsync, isPending: isCreating} = useMutation({
		mutationFn: createCustomer,
	});

	const {mutateAsync: updateCustomerAsync, isPending: isUpdating} = useMutation({
		mutationFn: updateCustomer,
	});

	const onSave = useCallback(async () => {
		const customerData = {
			name: name.trim(),
			email: email.trim(),
			phoneNumber: phoneNumber.trim(),
			notes: notes.trim(),
			address: address.trim(),
		};

		if (customer) {
			await updateCustomerAsync({
				id: customer.id,
				...customerData,
			});

			await refetchCustomer();
		} else {
			const newCustomer = await createCustomerAsync(customerData);

			window.location.href = `/customers/${newCustomer.id}/edit`;
		}
	}, [
		createCustomerAsync,
		updateCustomerAsync,
		refetchCustomer,
		customer,
		name,
		email,
		phoneNumber,
		notes,
		address,
	]);

	return (
		<Layout>
			<Layout.Header title={customer ? 'Edit Customer' : 'New Customer'}>
				<LoaderButton
					appearance='primary'
					disabled={saveDisabled}
					onClick={onSave}
					isLoading={isCreating || isUpdating}
					minWidth={80}
				>
					{customer ? 'Save' : 'Create'}
				</LoaderButton>
			</Layout.Header>
			<Layout.Body>
				{customerId && (
					<FormField label='Customer ID' maxWidth='300px'>
						{(props) => (
							<Input {...props} name='customerId' value={customerId!} disabled readOnly />
						)}
					</FormField>
				)}

				<FormField label='Name' maxWidth='300px'>
					{(props) => (
						<Input {...props} name='customerName' value={name} onChange={onNameChange} required />
					)}
				</FormField>

				<FormField label='Email' maxWidth='300px'>
					{(props) => (
						<Input
							{...props}
							name='customerEmail'
							type='email'
							value={email}
							onChange={onEmailChange}
						/>
					)}
				</FormField>

				<FormField label='Phone Number' maxWidth='300px'>
					{(props) => (
						<Input
							{...props}
							name='phoneNumber'
							value={phoneNumber}
							onChange={onPhoneNumberChange}
						/>
					)}
				</FormField>

				<FormField label='Address'>
					{(props) => (
						<Box display='flex' alignItems='center' columnGap='space.300'>
							<Box flex='1' maxWidth='400px'>
								<AddressAutocomplete
									{...props}
									name='address'
									value={address}
									onChange={onAddressChange}
								/>
							</Box>
							{address.trim() && (
								<Button
									appearance='secondary'
									onClick={() =>
										window.open(
											`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address.trim())}`,
											'_blank',
										)
									}
								>
									Directions
								</Button>
							)}
						</Box>
					)}
				</FormField>

				<FormField label='Notes'>
					{(props) => <Textarea {...props} name='notes' value={notes} onChange={onNotesChange} />}
				</FormField>
			</Layout.Body>
		</Layout>
	);
};

export default CustomerEdit;
